'use strict'
/* global google, cf */

/**
 * Manage Map
 * @module map
 */

import { addScript, getLang } from '../utils/utils'

const googleMapRegion = { fr: 'fr', uk: 'en-gb', nl: 'nl', de: 'de', es: 'es' }
let region = document.documentElement.lang
const SCRIPT_GOOGLE_MAP = 'https://maps.googleapis.com/maps/api/js?region='
                          + googleMapRegion[region]
                          + '&language=' + googleMapRegion[region]
                          + '&key=' + cf.gmaps.key
                          + '&callback=initMap'

const CLASS_THEMATIC = 'js-thematic'
const CLASS_FAVORITE = 'js-favorite'
const PREFIX_LANG = getLang()
const PREFIX_SEARCH = `${PREFIX_LANG}/api/search`
const PREFIX_SEARCH_FAVORITE = `${PREFIX_SEARCH}/favorite`
const PREFIX_THEMATIC = `${PREFIX_LANG}/api/thematic`

let map = null
let markers = []
let infoMarker = null
let backdrop = document.querySelector('#js-backdrop')
let mapContainer = document.querySelector('#js-map-wrapper')
let thematicMain = document.querySelector('main.js-thematic')

const mainClassList = document.querySelector('main').classList
const apiPrefix = `${mainClassList.contains(CLASS_THEMATIC) ? PREFIX_THEMATIC : (
  mainClassList.contains(CLASS_FAVORITE) ? PREFIX_SEARCH_FAVORITE : PREFIX_SEARCH
)}`

export default {

  campsites: [],
  campsitesCurrentOffset: 0,
  campsitesDisplayLimit: 9,
  apiCampsitesUrl: `/api/search/map/campsites`,
  campsitesModalContainer: document.getElementById('js-campsites-modal'),
  campsitesModalPrevCampsiteBtn: document.getElementById('js-campsites-nav-prev-modal'),
  campsitesModalNextCampsiteBtn: document.getElementById('js-campsites-nav-next-modal'),

  /**
   * Call function init()
   * @function init
   * @return {void}
   */
  init() {
    // Init params
    this.initSearchMapParam()
    this.scriptAdded = false
    this.bindClick()
    window.onInfoWindowClick = this.onInfoWindowClick
    window.initMap = this.initMap.bind(this)
  },

  onInfoWindowClick(e) {
    if (e.target.classList.value.indexOf('affichage_coordonnee') !== -1) {
      const elem = e.target
      const links = elem.parentElement.getElementsByClassName('display-number')
      links[0].style.display = 'none'
      links[links.length -1].style.display = 'block'
    }
  },

  /**
   * Init API Param
   * @function initSearchMapParam
   * @return {void}
   */
  initSearchMapParam() {
    this.apiParams = {}
    const filters = {}
    document.querySelector('.js-search').dataset.filterInit.split('&').forEach(item => {
      const data = item.split('=')
      filters[data[0]] = data[1]
    })
    const stateSelected = filters.state
    const countySelected = filters.county
    const citySelected = filters.city

    if (thematicMain) {
      this.apiParams.thematic_id = thematicMain.dataset.thematicId
        ? [thematicMain.dataset.thematicId]
        : ''
    }

    if (citySelected && countySelected && stateSelected) {
      this.apiParams.state = stateSelected
      this.apiParams.county = countySelected
      this.apiParams.city = citySelected
      this.requestType = 'camps'
      return
    }

    if (countySelected && stateSelected) {
      this.apiParams.state = stateSelected
      this.apiParams.county = countySelected
      this.requestType = 'cities'
      return
    }

    if (stateSelected) {
      this.apiParams.state = stateSelected
      this.requestType = 'counties'
      return
    }

    this.requestType = 'states'
  },

  /**
   * @function bindClick
   * @return {void}
   */
  bindClick() {
    // Prepare map Modal and Open it
    const jsMapOpen = document.getElementById('js-map-open')
    if (jsMapOpen) {
      jsMapOpen.addEventListener('click', this.prepareModal.bind(this))
    }
    // Close map Modal
    document.getElementById('js-map-close').addEventListener('click', this.closeModal.bind(this))
    // Show near Markers
    if (document.getElementById('checkbox-camping-near')) {
      document.getElementById('checkbox-camping-near')
              .addEventListener('change', this.showCampingNearMarker.bind(this))
    }
    this.campsitesModalPrevCampsiteBtn.addEventListener('click', this.displayPreviousPage.bind(this))
    this.campsitesModalNextCampsiteBtn.addEventListener('click', this.displayNextPage.bind(this))
  },

  displayNextPage() {
    this.campsitesCurrentOffset += 5
    this.processCampsites()
  },

  displayPreviousPage() {
    this.campsitesCurrentOffset -= 5
    this.processCampsites()
  },

  /**
   * Prepare modal
   * @function prepareModal
   * @return {void}
   */
  prepareModal() {
    if (!this.scriptAdded) {
      addScript(SCRIPT_GOOGLE_MAP)
      this.scriptAdded = true
      return
    }
    // Active map Modal
    mapContainer.classList.add('activ')

    // Show map modal
    backdrop.classList.add('activ')
    google.maps.event.trigger(map, 'resize')

    this.fetchMarker(this.getApiMapUrl(), this.markerClick.bind(this))
    this.fetchCampsites()
  },

  /**
   * Close Map Modal
   * @function closeModal
   * @return {void}
   */
  closeModal() {
    // Click to close Modal
    backdrop.classList.remove('activ')
    mapContainer.classList.remove('activ')
    this.removeAllMarkers()
  },

  /**
   * @function initMap
   * @return {void}
   */
  initMap() {
    const mapModal = document.getElementById('js-map-modal')
    mapModal.addEventListener('click', e => window.onInfoWindowClick(e))
    map = new google.maps.Map(mapModal, {
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      scrollwheel: true,
      mapTypeControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER
      },
      center: { lat: 46.9027, lng: 2.427275 },
      zoom: 7
    })
    this.prepareModal()
  },


  /**
   * Returns the URL of the API
   * @method getApiUrl
   * @return {string} - API's URL
   */
  getApiMapUrl() {
    return `${apiPrefix}/map/${this.requestType}.json`
  },


  /**
   * Function to call when marker was clicked,
   * update api params and laod Marker
   * @method markerClick
   * @param {Object} args - item marker data
   * @param {Object} marker - google map marker object
   * @return {void}
   */
  markerClick(args, marker) {
    let params = {}
    let loadMarker = true

    switch (this.requestType) {
      case 'states':
      default:
        this.requestType = 'counties'
        params = { state: args.slug }
        break
      case 'counties':
        this.requestType = 'cities'
        params = { county: args.slug }
        break
      case 'cities':
        this.requestType = 'camps'
        params = { city: args.slug }
        break
      case 'camps':
        this.showCampInfo(args.html, marker)
        loadMarker = false
        break
    }

    this.updateAPIparams(params)

    // Looad marker
    if (loadMarker) {
      this.removeAllMarkers()
      this.fetchMarker(this.getApiMapUrl(), this.markerClick.bind(this))
    }
  },

  /**
   * Show Camping Marker Info Window
   * @method showCampInfo
   * @param {String} htmlInfo - info camping content
   * @param {Object} marker - google map marker object
   * @return {void}
   */
  showCampInfo(htmlInfo, marker) {
    if (infoMarker) {
      infoMarker.close()
    }
    infoMarker = new google.maps.InfoWindow({ content: htmlInfo })
    infoMarker.open(map, marker)
  },

  /**
   * Show Camping near Marker
   * @method showCampingNearMarker
   * @param {Object} e - Event element
   * @return {void}
   */
  showCampingNearMarker(e) {
    this.removeAllMarkers()
    if (e.target.checked) {
      this.updateAPIparams({ nearby: '1' })
    } else {
      delete this.apiParams.nearby
    }
    this.fetchMarker(this.getApiMapUrl(), this.markerClick.bind(this))
  },

  /**
   * Update api_parms values by the argument
   * @method updateAPIparams
   * @param {Object} args - params to update
   * @return {void}
   */
  updateAPIparams(args) {
    for (let prop in args) {
      this.apiParams[prop] = args[prop]
    }
  },

  /**
   * Fetch Markes
   * @method fetchMarker
   * @param {String} url - API url
   * @param {'requestCallback'} func - callback function
   * @return {void}
   */
  fetchMarker(url, func) {
    this.getData(url, this.serializeQuery(this.apiParams), json => {
      this.processJson(json, func)
    })
  },

  fetchCampsites() {
    const params = this.serializeQuery(this.apiParams)
    this.getData(this.apiCampsitesUrl, params , json => {
      this.campsites = json
      this.processCampsites()
    })
  },

  processCampsites()
  {
    this.campsitesModalContainer.innerHTML = ''
    this.campsites.slice(this.campsitesCurrentOffset, this.campsitesCurrentOffset + this.campsitesDisplayLimit)
        .forEach(campsite => {
          this.appendCampsiteBlock(campsite)
        })
    this.showElement(this.campsitesModalPrevCampsiteBtn, this.campsitesCurrentOffset > 0)
    this.showElement(
        this.campsitesModalNextCampsiteBtn,
        this.campsitesCurrentOffset <= this.campsites.length - this.campsitesDisplayLimit
    )
  },

  showElement(elt, bool) {
    bool ? elt.style.display = 'inline' : elt.style.display = 'none'
  },

  appendCampsiteBlock(campsite) {
    const article = document.createElement('article')
    article.classList.add('CampingTeaser')
    article.classList.add('CampingTeaser--small')


    const imageLink = document.createElement('a')
    imageLink.href = campsite.url
    imageLink.title = campsite.campsite_name
    imageLink.classList.add('CampingTeaser-image')
    const figure = document.createElement('figure')
    figure.classList.add('visu-art')
    const picture = document.createElement('picture')
    const img = document.createElement('img')
    img.src = campsite.linknet_main_asset.length > 0 ? campsite.linknet_main_asset : '/img/ph-vert.png?v=1'
    img.itemprop = 'image'
    picture.appendChild(img)
    figure.appendChild(picture)
    imageLink.appendChild(figure)
    article.appendChild(imageLink)

    const div = document.createElement('div')
    div.classList.add('CampingTeaser-text')
    const link = document.createElement('a')
    link.href = campsite.url
    const h3 = document.createElement('h3')
    h3.classList.add('CampingTeaser-title')
    const spanName = document.createElement('span')
    spanName.textContent = campsite.campsite_name
    const spanStars = document.createElement('span')
    spanStars.classList.add('stars')
    for (let i = 0; i < campsite.classmnt_nbstars; i++) {
      const svg = document.createElement('span')
      svg.innerHTML = '<svg class="Icon Icon--etoile"><use xlink:href="/svg/sprite.svg#svg-etoile"></use></svg>'
      spanStars.appendChild(svg)
    }
    const geodata = document.createElement('span')
    geodata.classList.add('cf-city-dep')
    geodata.innerHTML = '<svg class="Icon Icon--gps"><use xlink:href="/svg/sprite.svg#svg-gps"></use></svg>'
    const spanGeoTxt = document.createElement('span')
    spanGeoTxt.classList.add('txt')
    spanGeoTxt.innerHTML = `<strong>${campsite.geodata.city}, </strong>${campsite.geodata.admin} (${campsite.geodata.admin_code})`
    geodata.appendChild(spanGeoTxt)

    h3.appendChild(spanName)
    h3.appendChild(spanStars)
    link.appendChild(h3)
    link.appendChild(geodata)
    div.appendChild(link)
    article.appendChild(div)

    this.campsitesModalContainer.appendChild(article)
  },

  /**
   * Serialize Query params
   * @method serializeQuery
   * @param {Object} params - Object to convert in string query
   * @return {String} query string
   */
  serializeQuery(params) {
    const usp = new URLSearchParams(params)
    return '?' + usp.toString()
  },

  /**
   * Get Data JSON
   * @method getData
   * @param {String} url - API url
   * @param {Object} params - params request
   * @param {'requestCallback'} func - callback function
   * @return {string} JSON responses
   */
  getData(url, params, func) {
    return fetch(url + params, {
      method: 'GET'
    })
      .then(
        response => {
          let retour = null
          switch (response.status) {
            case 200:
              retour = response.json()
              break
            default:
              retour = { datas: [], status: response.status, message: response.text() }
              break
          }
          return retour
        }
      )
      .then(json => {
        func(json)
        return json
      })
      .catch(() => {})
  },

  /**
   * Just a wrapper for processing the json received and adding the markers
   *
   * @param {Object} json - Json data received from server
   * @param {Function} func - Callback function
   * @return {void}
   */
  processJson(json, func) {
    const aggParamsKey = ['state', 'county', 'city']
    json.items.forEach((item) => {
      if (item.coord_latitude && item.coord_longitude) {
        this.addMarkerToMap(
          {
            lat: item.coord_latitude,
            lng: item.coord_longitude,
            title: item.label,
            nbrCampings: item.nbr_campings
          }, func, {
            slug: item.slug,
            html: item.html
          }
        )
      }
    })

    if (markers.length > 0) {
      let bounds = new google.maps.LatLngBounds()
      for (let i = 0; i < markers.length; i++) {
        bounds.extend(markers[i].getPosition())
      }
      map.fitBounds(bounds)
    }

    this.requestType = json.agg_level
    aggParamsKey.forEach(item => {
      if (json.agg_params[item]) {
        this.apiParams[item] = json.agg_params[item]
      }
    })
    this.updateDestination(json.items)
  },

  /**
   * Add Markers item to Map
   * @method addMarkerToMap
   * @param {Object} item - marker item
   * @param {'requestCallback'} func - callback function
   * @param {Object} args - callback arguments
   * @return {void}
   */
  addMarkerToMap(item, func, args) {
    let myLatLng = { lat: parseFloat(item.lat), lng: parseFloat(item.lng) }
    let marker = new google.maps.Marker(
      {
        position: myLatLng,
        map: map,
        title: item.title,
        label: item.nbrCampings && (item.nbrCampings).toString()
      }
    )
    markers.push(marker)
    // To add the marker to the map, call setMap()
    marker.setMap(map)

    marker.addListener('click', () => {
      func(args, marker)
    })
  },

  /**
   * Remove All Markers
   * @method removeAllMarkers
   * @return {void}
   */
  removeAllMarkers() {
    markers.forEach(function (m) {
      m.setMap(null)
    })
    markers = []
  },


  /**
   * Update destination dropdown
   * @method updateDestination
   * @param {Object} items - data to update
   * @return {void}
   */
  updateDestination(items) {
    switch (this.requestType) {
      case 'counties':
        if (regionDropdown) {
          regionDropdown.value = this.apiParams.state
        }
        if (departementDropdown) {
          this.insertOptionElement(departementDropdown, items, this.apiParams.state)
        }
        break
      case 'cities':
        if (departementDropdown) {
          departementDropdown.value = this.apiParams.county
        }
        if (communeDropdown) {
          this.insertOptionElement(
            communeDropdown,
            items,
            this.apiParams.state + '/' + this.apiParams.county
          )
        }
        break
      case 'camps':
        if (communeDropdown) {
          communeDropdown.value = this.apiParams.city
          communeDropdown.removeAttribute('disabled')
        }
        break
      default:
    }
  },

  /**
   * Insert option element to the dropdown
   * @method insertOptionElement
   * @param {Object} select - select element
   * @param {Object} items - json items
   * @param {String} url - option url
   * @return {void}
   */
  insertOptionElement(select, items, url = '') {
    let firstElement = select.firstChild
    select.innerHTML = ''
    select.appendChild(firstElement)
    items.forEach((item) => {
      let opt = document.createElement('option')
      opt.value = item.slug
      opt.setAttribute('data-url', `${regionDropdown.children[0].getAttribute('data-url')}/${url}/${item.slug}`)
      opt.innerHTML = item.label
      select.appendChild(opt)
    })
  }
}



// WEBPACK FOOTER //
// ./src/apps/Front/private/js/components/searchmap.js